import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { cacheService } from '../services/cacheService';

interface CmsUserData {
  cmsName: string;
  logoUrl?: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  cmsSettings?: {
    theme: string;
    language: string;
    customDomain?: string;
    binstreamApiUrl?: string;
  };
  slug: string;
  isActive: boolean;
}

interface CmsContextType {
  currentCms: CmsUserData | null;
  loading: boolean;
  error: string | null;
  loadCmsData: (slug: string) => Promise<void>;
  isPersonalizedRoute: boolean;
  clearCmsData: () => void;
}

const CmsContext = createContext<CmsContextType | undefined>(undefined);

export const CmsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentCms, setCurrentCms] = useState<CmsUserData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedSlugs, setLoadedSlugs] = useState<Set<string>>(new Set());
  const location = useLocation();

  // Carregar dados persistidos do localStorage na inicialização
  useEffect(() => {
    const savedCms = localStorage.getItem('cms_current');
    const savedSlugs = localStorage.getItem('cms_loaded_slugs');
    
    if (savedCms) {
      try {
        const cmsData = JSON.parse(savedCms);
        setCurrentCms(cmsData);
        
        // Aplicar cores imediatamente quando carregar do localStorage
        if (cmsData.colors) {
          const root = document.documentElement;
          root.style.setProperty('--cms-primary', cmsData.colors.primary);
          root.style.setProperty('--cms-secondary', cmsData.colors.secondary);
          root.style.setProperty('--cms-accent', cmsData.colors.accent);
          root.style.setProperty('--cms-background', cmsData.colors.background);
          root.style.setProperty('--cms-text', cmsData.colors.text);
          
          // Disparar evento para sincronizar com ColorContext
          if (window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('cmsColorsUpdated', {
              detail: { colors: cmsData.colors }
            }));
          }
        }
      } catch (error) {
        // Erro ao carregar CMS do localStorage
      }
    }
    
    if (savedSlugs) {
      try {
        const slugs = JSON.parse(savedSlugs);
        setLoadedSlugs(new Set(slugs));
      } catch (error) {
        // Erro ao carregar slugs do localStorage
      }
    }
  }, []);

  const isPersonalizedRoute = true;

  // Aplicar cores sempre que currentCms mudar
  useEffect(() => {
    if (currentCms && currentCms.colors) {
      const root = document.documentElement;
      root.style.setProperty('--cms-primary', currentCms.colors.primary);
      root.style.setProperty('--cms-secondary', currentCms.colors.secondary);
      root.style.setProperty('--cms-accent', currentCms.colors.accent);
      root.style.setProperty('--cms-background', currentCms.colors.background);
      root.style.setProperty('--cms-text', currentCms.colors.text);
      
      // Aplicar cores também no ColorContext para sincronização
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('cmsColorsUpdated', {
          detail: { colors: currentCms.colors }
        }));
      }
    }
  }, [currentCms]);

  // Função para limpar dados do CMS
  const clearCmsData = useCallback(() => {
    setCurrentCms(null);
    setLoadedSlugs(new Set());
    localStorage.removeItem('cms_current');
    localStorage.removeItem('cms_loaded_slugs');
  }, []);

  const loadDefaultCmsData = useCallback(async () => {
    // Removido carregamento de dados padrão - cada revendedor deve ter seu próprio slug
    // Não carregar dados padrão para evitar valores genéricos
    return;
  }, []);

  const loadCmsData = useCallback(async (slug: string) => {
    if (!slug) {
      return;
    }
    
    if (loadedSlugs.has(slug)) {
      return;
    }

    setLoading(true);
    setError(null);

    // Marcar slug como sendo carregado
    setLoadedSlugs(prev => {
      const newSet = new Set(prev);
      newSet.add(slug);
      
      // Persistir slugs no localStorage
      localStorage.setItem('cms_loaded_slugs', JSON.stringify(Array.from(newSet)));
      
      return newSet;
    });

    try {
      // Verificar cache primeiro
      const cacheKey = cacheService.generateKey('cms', { slug });
      const cachedData = cacheService.get(cacheKey);
      
      if (cachedData) {
        setCurrentCms(cachedData as CmsUserData);
        setLoading(false);
        return;
      }

      // Sempre usar rota pública para obter dados do CMS baseado no slug
      // Isso garante que sub-revendedores vejam o ambiente do sócio
      // Implementa fallback automático com proxy
      const getApiBaseUrl = () => {
        if (process.env.REACT_APP_API_URL) {
          return process.env.REACT_APP_API_URL;
        }
        return 'https://api-cms.cms20.online';
      };
      
      const PROXY_BASE_URL = 'https://xms-proxy.u9ionsrv.workers.dev';
      const apiBaseUrl = getApiBaseUrl();
      
      let response;
      
      // Tentar primeiro com a API principal
      try {
        // Timeout de 5 segundos para evitar travamento
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        const headers: any = {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };
        
        response = await fetch(`${apiBaseUrl}/api/cms/${slug}/public`, {
          method: 'GET',
          headers: headers,
          credentials: 'include',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        // Se receber erro 403, 5xx ou qualquer erro não-ok, tentar proxy
        if (!response.ok) {
          const errorStatus = response.status;
          const shouldTryProxy = errorStatus === 403 || errorStatus >= 500;
          
          if (shouldTryProxy) {
            console.warn(`⚠️ [CmsContext] Erro ${errorStatus} com API principal. Tentando proxy...`);
            try {
              const proxyResponse = await fetch(`${PROXY_BASE_URL}/api/cms/${slug}/public`, {
                method: 'GET',
                headers: headers,
                credentials: 'include'
              });
              
              if (proxyResponse.ok) {
                console.log(`✅ [CmsContext] Dados obtidos via proxy com sucesso`);
                response = proxyResponse;
              } else {
                console.warn(`⚠️ [CmsContext] Proxy também retornou erro ${proxyResponse.status}`);
                // Se o proxy também falhar, response permanece com o erro original
                // para que o código abaixo tente usar dados padrão
              }
            } catch (proxyError: any) {
              console.error(`❌ [CmsContext] Erro ao buscar dados via proxy:`, proxyError);
              // Se o proxy falhar completamente, response permanece com o erro original
            }
          }
        }
      } catch (fetchError: any) {
        // Se foi abortado por timeout, erro de rede ou conexão, tentar proxy
        const isConnectionError = 
          fetchError.name === 'AbortError' ||
          fetchError.message?.includes('Network Error') ||
          fetchError.message?.includes('Failed to fetch') ||
          fetchError.message?.includes('timeout');
        
        if (isConnectionError) {
          console.warn(`⚠️ [CmsContext] Erro de conexão com API principal. Tentando proxy...`);
          try {
            response = await fetch(`${PROXY_BASE_URL}/api/cms/${slug}/public`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              credentials: 'include'
            });
            if (response.ok) {
              console.log(`✅ [CmsContext] Dados obtidos via proxy com sucesso`);
            } else {
              console.warn(`⚠️ [CmsContext] Proxy retornou erro ${response.status}. Usando dados padrão...`);
              response = null; // Será tratado abaixo para usar dados padrão
            }
          } catch (proxyError: any) {
            console.error(`❌ [CmsContext] Erro ao buscar dados via proxy:`, proxyError);
            console.warn(`⚠️ [CmsContext] Usando dados padrão devido a falha completa de conexão...`);
            response = null; // Será tratado abaixo para usar dados padrão
          }
        } else {
          console.error(`❌ Erro ao carregar CMS ${slug}:`, fetchError);
          console.warn(`⚠️ [CmsContext] Usando dados padrão devido a erro desconhecido...`);
          response = null; // Será tratado abaixo para usar dados padrão
        }
      }
      
      if (response && response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          
          // Salvar token se retornado na resposta
          
          if (data.cms) {
            const cmsData = {
              cmsName: data.cms.name,
              logoUrl: data.cms.logoUrl,
              colors: {
                primary: data.cms.colors?.primary,
                secondary: data.cms.colors?.secondary,
                accent: data.cms.colors?.accent,
                background: data.cms.colors?.background,
                text: data.cms.colors?.text
              },
              cmsSettings: {
                theme: data.cms.settings?.theme || 'light',
                language: data.cms.settings?.language || 'pt-BR',
                customDomain: data.cms.settings?.customDomain,
                binstreamApiUrl: data.cms.settings?.binstreamApiUrl
                  || data.cms.cmsSettings?.binstreamApiUrl
              },
              slug: slug,
              isActive: true
            };
            
            // Armazenar no cache por 10 minutos
            cacheService.set(cacheKey, cmsData, 10 * 60 * 1000);
            
            // Persistir no localStorage
            localStorage.setItem('cms_current', JSON.stringify(cmsData));
            
            setCurrentCms(cmsData);
            return;
          }
        }
      } else if (response) {
        // Se a rota pública falhou (mesmo após tentar proxy), usar dados mínimos
        console.warn(`⚠️ [CmsContext] Requisição falhou com status ${response.status}. Usando dados mínimos...`);
        setError(null); // Limpar erro
        const minimalCmsData: CmsUserData = {
          cmsName: slug || 'CMS Panel',
          logoUrl: '',
          colors: {
            primary: '#3B82F6',
            secondary: '#1E40AF',
            accent: '#F59E0B',
            background: '#F8FAFC',
            text: '#1F2937'
          },
          slug: slug || 'u9ion',
          isActive: true
        };
        setCurrentCms(minimalCmsData);
        localStorage.setItem('cms_current', JSON.stringify(minimalCmsData));
        return;
      } else {
        // Se não houve resposta (erro de conexão completo), usar dados mínimos
        console.warn(`⚠️ [CmsContext] Não foi possível conectar à API. Usando dados mínimos...`);
        setError(null); // Limpar erro
        const minimalCmsData: CmsUserData = {
          cmsName: slug || 'CMS Panel',
          logoUrl: '',
          colors: {
            primary: '#3B82F6',
            secondary: '#1E40AF',
            accent: '#F59E0B',
            background: '#F8FAFC',
            text: '#1F2937'
          },
          slug: slug || 'u9ion',
          isActive: true
        };
        setCurrentCms(minimalCmsData);
        localStorage.setItem('cms_current', JSON.stringify(minimalCmsData));
        return;
      }

    } catch (err) {
      console.error(`❌ Erro ao carregar dados do CMS (${slug}):`, err);
      
      // Fallback para dados padrão do u9ion
      try {
        setError(null); // Limpar erro antes de tentar dados padrão
        await loadDefaultCmsData();
      } catch (fallbackError) {
        console.error('❌ Erro no fallback de CMS:', fallbackError);
        // Se até o fallback falhar, usar dados mínimos e definir erro apenas se realmente necessário
        const minimalCmsData: CmsUserData = {
          cmsName: 'U9ion CMS',
          logoUrl: '',
          colors: {
            primary: '#3B82F6',
            secondary: '#1E40AF',
            accent: '#F59E0B',
            background: '#F8FAFC',
            text: '#1F2937'
          },
          slug: slug || 'u9ion',
          isActive: true
        };
        setCurrentCms(minimalCmsData);
        localStorage.setItem('cms_current', JSON.stringify(minimalCmsData));
        // Não definir erro mesmo se o fallback falhar - usar dados mínimos silenciosamente
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  }, [loadedSlugs, loadDefaultCmsData]);

  // Função para extrair slug da URL - sempre retorna um slug
  const extractSlugFromPath = useCallback((path: string): string => {
    // Extrair o primeiro segmento após /
    const segments = path.split('/');
    const possibleSlug = segments[1];
    
    // Se não tiver slug ou for uma rota especial, usar u9ion
    if (!possibleSlug || possibleSlug === '') {
      return 'u9ion';
    }
    
    // Lista de rotas que NÃO são slugs (rotas especiais)
    const nonSlugRoutes = ['login', 'api', 'static', 'assets', 'favicon.ico', 'manifest.json', 'robots.txt'];
    
    // Se for uma rota especial, usar u9ion
    if (nonSlugRoutes.includes(possibleSlug)) {
      return 'u9ion';
    }
    
    return possibleSlug;
  }, []);

  // Carregar dados do CMS quando o componente monta ou a rota muda
  useEffect(() => {
    const currentPath = location.pathname;

    // Sempre extrair um slug (u9ion como padrão)
    const detectedSlug = extractSlugFromPath(currentPath);

    // Se o slug detectado não foi carregado ainda, carregar
    if (!loadedSlugs.has(detectedSlug)) {
      loadCmsData(detectedSlug);
    }
  }, [location.pathname, loadCmsData, loadedSlugs, extractSlugFromPath]);

  // Escutar evento de login para carregar dados do CMS
  useEffect(() => {
    const handleUserLogin = (event: CustomEvent) => {
      const { cmsSlug } = event.detail;
      console.log(`Evento de login recebido para CMS: ${cmsSlug}`);
      
      if (cmsSlug && !loadedSlugs.has(cmsSlug)) {
        console.log(`Carregando dados do CMS após login: ${cmsSlug}`);
        loadCmsData(cmsSlug);
      }
    };

    window.addEventListener('userLoggedIn', handleUserLogin as EventListener);
    
    return () => {
      window.removeEventListener('userLoggedIn', handleUserLogin as EventListener);
    };
  }, [loadCmsData, loadedSlugs]);

  return (
    <CmsContext.Provider value={{
      currentCms,
      loading,
      error,
      loadCmsData,
      isPersonalizedRoute,
      clearCmsData
    }}>
      {children}
    </CmsContext.Provider>
  );
};

export const useCms = () => {
  const context = useContext(CmsContext);
  if (context === undefined) {
    throw new Error('useCms must be used within a CmsProvider');
  }
  return context;
};
